import React, { useEffect, useRef, useState } from "react";
import { AccountLayout } from "./AccountLayout";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { useAuth } from "../../lib/auth";
import api from "../../lib/api";
import { toast } from "sonner";
import { ShieldCheck, RefreshCw, AlertOctagon, FileCheck2, Clock, CircleSlash, RotateCcw } from "lucide-react";
import VpnWarning from "../VpnWarning";

/**
 * Phase 0 KYCAID document-verification screen.
 *
 * Replaces the fictional €150/€2k/€50k tier ladder with a single document flow:
 *   unverified → submitted → verified | failed
 *
 * Liveness sits with XGate's Sumsub at the checkout step — NOT here. KYCAID is
 * intentionally lighter (ID + proof of address only).
 */
const STATE_INFO = {
  unverified: {
    label: "Not yet started",
    cls: "bg-slate-100 text-slate-600 border-slate-200",
    icon: FileCheck2,
    body: "Upload your ID document and proof of address to get verified. Most people are cleared within 15 minutes of submission.",
  },
  submitted: {
    label: "Submitted — under review",
    cls: "bg-sky-50 text-sky-700 border-sky-200",
    icon: Clock,
    body: "Your documents are with our verification partner. This usually completes within a few minutes. We'll notify you the moment the result lands.",
  },
  in_progress: {
    label: "Started — not yet submitted",
    cls: "bg-amber-50 text-amber-800 border-amber-200",
    icon: RotateCcw,
    body: "You opened the verification form but didn't finish uploading. Pick up where you left off — your progress on our side is saved. Nothing has been sent for review yet.",
  },
  verified: {
    label: "Verified",
    cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
    icon: ShieldCheck,
    body: "All set. A staff member will reach out to issue your first purchase invoice. We don't display monetary tier limits — the real limits sit with your payment partner at checkout.",
  },
  failed: {
    label: "Needs attention",
    cls: "bg-rose-50 text-rose-700 border-rose-200",
    icon: AlertOctagon,
    body: "Your documents could not be cleared. See the reasons below and try again.",
  },
};

export default function KycPage() {
  const { user, refreshMe } = useAuth();
  const [status, setStatus] = useState(null);
  const [formUrl, setFormUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/kycaid/status");
      setStatus(r.data);
    } catch (e) { /* silent */ }
    setLoading(false);
  };

  // Poll status:
  //  • While `submitted` (8s) — KYCAID callback drives the real flip, this
  //    just pulls fresh user state into the UI for a tighter feel.
  //  • While the provider is unavailable / `wired=false` (30s) — so the user
  //    sees the pill auto-clear once ops drops in credentials, without a
  //    manual page refresh.
  /* eslint-disable */
  useEffect(() => {
    load();
    pollRef.current = setInterval(() => {
      if (status?.state === "submitted") { load(); refreshMe(); }
      else if (status && !status.wired) { load(); }
    }, status?.state === "submitted" ? 8000 : 30000);
    return () => clearInterval(pollRef.current);
  }, [status?.state, status?.wired]);
  /* eslint-enable */

  const startVerification = async () => {
    setStarting(true);
    try {
      const r = await api.post("/kycaid/form-url");
      if (!r.data.wired) {
        toast.error("Verification is not yet wired in this environment. Operator must set KYCAID_API_TOKEN + KYCAID_FORM_ID.");
        return;
      }
      if (!r.data.form_url) {
        toast.error(r.data.error || "Could not fetch verification form. Please retry.");
        return;
      }
      setFormUrl(r.data.form_url);
      // Status will be 'submitted' after server-side state flip
      load();
      refreshMe();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start verification");
    } finally { setStarting(false); }
  };

  const state = status?.state || "unverified";
  const wired = !!status?.wired;
  const info = STATE_INFO[state] || STATE_INFO.unverified;
  const Icon = info.icon;
  const declineReasons = status?.decline_reasons || [];

  if (loading) {
    return <AccountLayout title="Verification"><div className="text-sm text-muted-foreground">Loading…</div></AccountLayout>;
  }

  return (
    <AccountLayout
      title="Verification"
      subtitle="A short document check on our side. Liveness happens later, when you check out at our payment partner."
    >
      <div className="space-y-5 max-w-3xl">
        <div className="xch-card p-5 flex items-start gap-4" data-testid="kycaid-status-card">
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 border ${info.cls}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-display font-bold text-foreground">{info.label}</div>
              <Badge variant="outline" className={`text-[10px] uppercase tracking-wider font-bold ${info.cls}`}>
                {state}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{info.body}</p>

            {!wired && (
              <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900"
                   role="status" aria-live="polite" data-testid="kycaid-unavailable-pill">
                <div className="flex items-start gap-2">
                  <CircleSlash className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                  <div className="leading-relaxed">
                    <div className="font-semibold">Verification temporarily unavailable</div>
                    <div className="mt-0.5">
                      Our identity-verification partner is offline right now. We&apos;ve been notified
                      and you don&apos;t need to do anything — please check back in a few minutes.
                      Your account is safe and no data has been lost.
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          {(state === "unverified" || state === "failed") && wired && (
            <Button onClick={startVerification} disabled={starting}
                    className="bg-primary hover:bg-primary/90 text-white rounded-xl gap-1.5 shrink-0"
                    data-testid="kycaid-start-btn">
              <ShieldCheck className="w-4 h-4" />
              {starting ? "Starting…" : (state === "failed" ? "Try again" : "Start verification")}
            </Button>
          )}
          {state === "in_progress" && wired && (
            <Button onClick={startVerification} disabled={starting}
                    className="bg-amber-600 hover:bg-amber-700 text-white rounded-xl gap-1.5 shrink-0"
                    data-testid="kycaid-resume-btn">
              <RotateCcw className="w-4 h-4" />
              {starting ? "Reopening…" : "Resume verification"}
            </Button>
          )}
          {(state === "unverified" || state === "failed" || state === "in_progress") && !wired && (
            <Button onClick={() => { load(); refreshMe(); }} variant="outline"
                    className="rounded-xl gap-1.5 shrink-0"
                    data-testid="kycaid-recheck-btn">
              <RefreshCw className="w-4 h-4" /> Check again
            </Button>
          )}
          {state === "submitted" && (
            <Button onClick={() => { load(); refreshMe(); }} variant="outline"
                    className="rounded-xl gap-1.5 shrink-0" data-testid="kycaid-refresh-btn">
              <RefreshCw className="w-4 h-4" /> Refresh
            </Button>
          )}
        </div>

        {(state === "unverified" || state === "failed") && <VpnWarning />}

        {state === "failed" && declineReasons.length > 0 && (
          <div className="xch-card p-4 bg-rose-50/60 border-rose-200" data-testid="kycaid-decline-reasons">
            <div className="text-sm font-display font-bold text-rose-900">Decline reasons</div>
            <ul className="mt-1.5 text-xs text-rose-900/85 space-y-1 list-disc pl-4">
              {declineReasons.map((r, i) => <li key={i}>{String(r)}</li>)}
            </ul>
          </div>
        )}

        {formUrl && (
          <div className="xch-card p-2 overflow-hidden" data-testid="kycaid-iframe-wrap">
            <iframe
              src={formUrl}
              title="Identity verification"
              allow="microphone *;camera *;midi *;encrypted-media *;clipboard-read;clipboard-write;"
              className="w-full rounded-xl"
              style={{ minHeight: 680, border: 0 }}
              data-testid="kycaid-iframe"
            />
            <div className="text-[10px] text-muted-foreground mt-1 px-2">
              Hosted by our verification partner. Documents are encrypted in transit and at rest.
            </div>
          </div>
        )}

        <div className="xch-card p-4 bg-muted/30" data-testid="kycaid-info-block">
          <div className="text-xs font-semibold text-foreground">What we ask for</div>
          <ul className="text-xs text-muted-foreground mt-1.5 space-y-1 list-disc pl-4">
            <li>A valid government-issued ID document (passport or national ID).</li>
            <li>A recent proof of address (utility bill or bank statement, &lt;3 months old).</li>
          </ul>
          <div className="text-[10px] text-muted-foreground mt-3 leading-relaxed">
            <strong>About the limits</strong> — Xchangely does not impose its own monetary tier limits. The real per-deposit and review thresholds sit with our regulated payment partner and are explained at checkout. We don&apos;t display fictional ladders here.
          </div>
        </div>
      </div>
    </AccountLayout>
  );
}
